import React from 'react';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import LinearProgress from '@material-ui/core/LinearProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { useInsightsStyles } from '../styles';
import { ExportContext } from '../exportContext';
import { formatCount, formatDateTime, formatMetric, formatPercent, isOverAssigned, relativeTime } from '../utils';
import StatusChip, { STATUS_CHIP_TONES, toneForStatus } from '../../../../Components/StatusChip';

export const TONES = {
  green: [STATUS_CHIP_TONES.green.background, STATUS_CHIP_TONES.green.color],
  amber: [STATUS_CHIP_TONES.amber.background, STATUS_CHIP_TONES.amber.color],
  red: [STATUS_CHIP_TONES.red.background, STATUS_CHIP_TONES.red.color],
  blue: [STATUS_CHIP_TONES.blue.background, STATUS_CHIP_TONES.blue.color],
  grey: [STATUS_CHIP_TONES.grey.background, STATUS_CHIP_TONES.grey.color],
};

const useSharedStyles = makeStyles({
  progressBar: { backgroundColor: '#3D7DDD' },
  dangerBar: { backgroundColor: '#dc2626' },
});

const useMetricGridStyles = makeStyles(theme => ({
  grid: props => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: `repeat(${Math.min(2, props.columns)}, minmax(0, 1fr))`,
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: `repeat(${props.columns}, minmax(0, 1fr))`,
    },
  }),
}));

const SectionSurfaceContext = React.createContext(false);

export const ExportButton = ({ exportKey, label = 'Export to Excel' }) => {
  const exporter = React.useContext(ExportContext);
  if (!exporter || !exportKey) return null;
  const busy = !!exporter.busy[exportKey];
  return (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      disabled={busy}
      onClick={() => exporter.exportSection(exportKey)}
      startIcon={busy ? <CircularProgress size={14} /> : <GetAppOutlinedIcon fontSize="small" />}
    >
      {label}
    </Button>
  );
};

export const Section = ({ title, titleAdornment, subtitle, action, exportKey, children, id, className = '' }) => {
  const classes = useInsightsStyles();
  return (
    <SectionSurfaceContext.Provider value>
      <section id={id} className={`${classes.section} ${className}`} aria-labelledby={id ? `${id}-title` : undefined}>
        <div className={classes.sectionHeader} data-testid={id ? `${id}-section-header` : undefined}>
          <div style={{ minWidth: 0, flex: '1 1 320px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Typography id={id ? `${id}-title` : undefined} component="h2" className={classes.sectionTitle}>{title}</Typography>
              {titleAdornment}
            </div>
            {subtitle ? <Typography className={classes.sectionSubtitle}>{subtitle}</Typography> : null}
          </div>
          <div className={classes.sectionActions}>
            {action}
            <ExportButton exportKey={exportKey} />
          </div>
        </div>
        {children}
      </section>
    </SectionSurfaceContext.Provider>
  );
};

export const Panel = ({ children, className = '', ...props }) => {
  const classes = useInsightsStyles();
  const inSection = React.useContext(SectionSurfaceContext);
  return <div className={`${classes.panel} ${inSection ? classes.sectionPanel : ''} ${className}`} {...props}>{children}</div>;
};

export const MetricTile = ({ label, value, unit = 'count', pct, description, note, warning = false, danger = false }) => {
  const classes = useInsightsStyles();
  const over = unit === 'percent' && isOverAssigned(value);
  const color = danger || over ? '#b91c1c' : warning ? '#b45309' : undefined;
  return (
    <div className={classes.tile} style={{ height: '100%' }}>
      <Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>{label}</Typography>
      <Typography className={classes.metricValue} style={color ? { color } : undefined}>
        {formatMetric(value, unit)}
        {pct !== undefined ? <span className={classes.muted} style={{ fontSize: '0.85rem', fontWeight: 600, marginLeft: 8 }}>{formatPercent(pct)}</span> : null}
      </Typography>
      {over ? <Badge tone="red">Over-assigned</Badge> : null}
      {note ? <Typography className={classes.description} style={{ marginTop: 2 }}>{note}</Typography> : null}
      {description ? <Typography className={classes.description}>{description}</Typography> : null}
    </div>
  );
};

export const MetricGrid = ({ items = [], columns = 4, description }) => {
  const effectiveColumns = Math.max(1, Math.min(columns, items.length));
  const metricGridClasses = useMetricGridStyles({ columns: effectiveColumns });
  if (!items.length) return null;
  return (
    <div className={metricGridClasses.grid} data-testid="metric-grid" data-columns={effectiveColumns}>
      {items.map((item, index) => (
        <div key={item.key || item.label || index} style={{ minWidth: 0 }}>
          <MetricTile
            label={item.label}
            value={item.value}
            unit={item.unit}
            pct={item.pct}
            note={item.note}
            description={item.description || description}
            warning={item.warning}
            danger={item.danger}
          />
        </div>
      ))}
    </div>
  );
};

export const Badge = ({ tone = 'grey', children }) => {
  return <StatusChip tone={tone}>{children}</StatusChip>;
};

const LEARNING_HUB_TONES = { active: 'green', scheduled: 'amber', expired: 'red', not_configured: 'grey' };

/** `learning_hub` / `access_state` values: active | scheduled | expired | not_configured. */
export const LearningHubBadge = ({ value }) => {
  const key = String(value || 'not_configured').toLowerCase();
  const label = key === 'not_configured' ? 'Not set up' : key.replace(/_/g, ' ');
  return <Badge tone={LEARNING_HUB_TONES[key] || 'grey'}>{label.charAt(0).toUpperCase() + label.slice(1)}</Badge>;
};

const ONBOARDING_TONES = { completed: 'green', in_progress: 'blue', not_started: 'amber', no_record: 'grey' };

export const OnboardingBadge = ({ status, label }) => (
  <Badge tone={ONBOARDING_TONES[status] || 'grey'}>{label || String(status || '-').replace(/_/g, ' ')}</Badge>
);

export const ProgressValue = ({ value, danger = false }) => {
  const sharedClasses = useSharedStyles();
  const numeric = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ minWidth: 96 }}>
      <LinearProgress
        variant="determinate"
        value={numeric}
        style={{ height: 5, borderRadius: 5, marginBottom: 4 }}
        classes={{ bar: danger ? sharedClasses.dangerBar : sharedClasses.progressBar }}
      />
      <span style={danger ? { color: '#b91c1c', fontWeight: 700 } : undefined}>{formatPercent(value)}</span>
    </div>
  );
};

/** Utilisation can exceed 100%: that is shown in red with the over-assigned state. */
export const UtilisationValue = ({ value, overAssigned }) => {
  const over = isOverAssigned(value, overAssigned);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <ProgressValue value={value} danger={over} />
      {over ? <Badge tone="red">Over-assigned</Badge> : null}
    </span>
  );
};

export const RelativeTime = ({ value }) => {
  if (!value) return '-';
  return (
    <Tooltip title={formatDateTime(value)} arrow>
      <time dateTime={value} tabIndex={0} style={{ whiteSpace: 'nowrap', cursor: 'help' }}>{relativeTime(value)}</time>
    </Tooltip>
  );
};

export const InfoTip = ({ title, label, children }) => (
  <Tooltip title={title || ''} arrow interactive>
    <span tabIndex={0} role="button" aria-label={label || (typeof title === 'string' ? title : 'More information')} style={{ display: 'inline-flex', cursor: 'help' }}>
      {children || <InfoOutlinedIcon fontSize="small" />}
    </span>
  </Tooltip>
);

export const Callout = ({ title, body, children, info = false }) => {
  const classes = useInsightsStyles();
  return (
    <div className={classes.callout} style={info ? { borderColor: 'rgba(61,125,221,.35)', background: 'rgba(61,125,221,.08)' } : undefined}>
      {info ? <InfoOutlinedIcon color="primary" /> : <WarningRoundedIcon style={{ color: '#b45309' }} />}
      <div style={{ minWidth: 0 }}>
        {title ? <Typography style={{ fontWeight: 700 }}>{title}</Typography> : null}
        {body ? <Typography className={classes.description} style={title ? undefined : { marginTop: 0 }}>{body}</Typography> : null}
        {children}
      </div>
    </div>
  );
};

/** Full-panel error showing the API's `message`. */
export const ErrorPanel = ({ title, message, action }) => (
  <Panel role="alert" style={{ minHeight: 320, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
    <div style={{ maxWidth: 520 }}>
      <ErrorOutlineIcon color="error" style={{ fontSize: 44 }} />
      <Typography component="h1" variant="h6" style={{ fontWeight: 700, marginTop: 8 }}>{title}</Typography>
      {message ? <Typography color="textSecondary" style={{ margin: '8px 0 16px' }} data-testid="error-message">{message}</Typography> : null}
      {action}
    </div>
  </Panel>
);

/** Shown in place of a chart or table when the API returns nothing; `children` is the API's own reason. */
export const EmptyState = ({ children }) => {
  const classes = useInsightsStyles();
  return <div className={classes.empty}>{children || 'No data available'}</div>;
};

export const StatRow = ({ items = [] }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {items.map(({ key, label, value, tone, description }) => {
      const palette = tone ? TONES[tone] : null;
      const chip = (
        <Chip
          key={key || label}
          variant={palette ? 'default' : 'outlined'}
          label={<span><strong>{formatCount(value)}</strong> {label}</span>}
          style={palette ? { background: palette[0], color: palette[1], fontWeight: 600 } : undefined}
        />
      );
      return description ? <Tooltip key={key || label} title={description} arrow>{chip}</Tooltip> : chip;
    })}
  </div>
);

export const DataTable = ({ columns, rows = [], onRowClick, ariaLabel, compact = false, emptyText, rowStyle, rowClassName, getRowKey }) => {
  const classes = useInsightsStyles();
  const renderCell = (column, row) => {
    if (column.render) return column.render(row);
    const value = row[column.key];
    if (
      ['status', 'status_label', 'state', 'state_label'].includes(column.key)
      && ['string', 'number'].includes(typeof value)
      && value !== ''
    ) {
      return <StatusChip tone={toneForStatus(value)}>{String(value).replace(/_/g, ' ')}</StatusChip>;
    }
    return value ?? '-';
  };

  return (
    <div className={classes.tableWrap} tabIndex={rows.length ? 0 : undefined} aria-label={rows.length ? `${ariaLabel} scrollable table` : undefined}>
      <Table size="small" className={classes.table} aria-label={ariaLabel} style={{ minWidth: compact ? 0 : Math.max(560, columns.length * 120) }}>
        <TableHead><TableRow>{columns.map(column => <TableCell key={column.key} align={column.align}>{column.label}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {rows.length ? rows.map((row, index) => (
            <TableRow
              key={getRowKey ? getRowKey(row, index) : (row.id || row.uuid || row.key || index)}
              className={[onRowClick ? classes.clickableRow : '', rowClassName ? rowClassName(row) : ''].filter(Boolean).join(' ') || undefined}
              style={rowStyle ? rowStyle(row) : undefined}
              hover={!!onRowClick}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? event => {
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onRowClick(row); }
              } : undefined}
            >
              {columns.map(column => <TableCell key={column.key} align={column.align}>{renderCell(column, row)}</TableCell>)}
            </TableRow>
          )) : (
            <TableRow><TableCell colSpan={columns.length} align="center" className={classes.muted}>{emptyText || 'No data available'}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

/** Red-tinted rows for over-assigned records. */
export const overAssignedRowStyle = row => (row.over_assigned ? { background: 'rgba(220,38,38,.06)' } : undefined);

export const HiddenChartTable = ({ caption, headers, rows }) => {
  const classes = useInsightsStyles();
  return (
    <div className={classes.srOnly}>
      <table>
        <caption>{caption}</caption>
        <thead><tr>{headers.map(header => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};
